// libs
import { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';

// analytics
import { updateAnalyticalData } from 'shared/routes/Detail/analytics';

// helpers
import { isDateValid } from 'shared/helpers/utils';

// hooks
import {
  useSelectedSKU,
  useSelectedSKUDiscount,
  useSelectedSKUDiscountPrice,
  useIsSelectedSKUOOS,
  useSelectedSKUPrice,
  useSelectedSKUSubTitle,
  useSelectedSKUTitle,
  useSelectedSKUPriceRevealData,
  useSelectedSKUSalePriceLiveConfig,
} from 'shared/store/details/hooks/selectors';
import { useSetSelectedSKU } from 'shared/store/details/hooks/actions';

//selectors
import { isOneSize, getFomoTag } from 'shared/store/details/selectors';
import { useDwebCartContext } from 'shared/components/DwebCart/context';

export const useProductInfo = () => {
  const [sizesAdded, setSizesAdded] = useState<string[]>([]);
  const { canShowCart } = useDwebCartContext();

  const history = useHistory();
  const location = useLocation();
  const setSelectedSKU = useSetSelectedSKU();
  const oneSize = useSelector(isOneSize);

  useEffect(() => {
    if (canShowCart) setSizesAdded([]);
  }, [canShowCart]);
  const setSelectedSize = useCallback(
    (size: ProductDetail.ISizeOption | undefined) => {
      if (size === undefined) {
        return;
      }

      const { pathname, search } = location;

      const searchParams = new URLSearchParams(search);
      if (searchParams.has('skuId') && searchParams.get('skuId') === size.id) {
        return;
      }

      if (searchParams.has('skuId')) {
        searchParams.set('skuId', size.id);
      } else {
        searchParams.append('skuId', size.id);
      }

      const newURL = `${pathname}?${searchParams.toString()}`;

      history.replace(newURL);
      setSelectedSKU({ skuId: size.id, oneSize: oneSize });
    },
    [history, location, oneSize, setSelectedSKU]
  );

  const selectedSize = useSelectedSKU();
  useEffect(() => {
    if (selectedSize !== undefined) {
      updateAnalyticalData(selectedSize);
    }
  }, [selectedSize, sizesAdded]);

  const addToCart = useCallback(() => {
    if (selectedSize === undefined) {
      return;
    }

    if (sizesAdded.includes(selectedSize.sizeName) === false) {
      setSizesAdded([...sizesAdded, selectedSize.sizeName]);
    }
  }, [selectedSize, sizesAdded]);

  const isOutOfStock = useIsSelectedSKUOOS();
  const displayTitle = useSelectedSKUTitle();
  const displaySubTitle = useSelectedSKUSubTitle();
  const displayPrice = useSelectedSKUPrice();
  const displayDiscount = useSelectedSKUDiscount();
  const displayDiscountedPrice = useSelectedSKUDiscountPrice();
  const priceRevealData = useSelectedSKUPriceRevealData();
  const isFomoTimerValid = isDateValid(useSelector(getFomoTag)?.end_time) || false;
  const isSalePriceLive = useSelectedSKUSalePriceLiveConfig();

  const isAddedToCart =
    selectedSize !== undefined ? sizesAdded.indexOf(selectedSize.sizeName) > -1 : false;

  return {
    isAddedToCart,
    isOutOfStock,
    displayTitle,
    displaySubTitle,
    displayPrice,
    displayDiscount,
    displayDiscountedPrice,
    selectedSize,
    setSelectedSize,
    addToCart,
    priceRevealData,
    isOneSize: oneSize,
    isFomoTimerVisible: isFomoTimerValid,
    isSalePriceLive,
  };
};
